import CloseIcon from '@mui/icons-material/Close';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import { Button, Tooltip } from '@mui/material';
import { useSignals } from '@preact/signals-react/runtime';
import { Feature, Geometry } from '@turf/helpers';
import { find, flatMap, includes } from 'es-toolkit/compat';
import React from 'react';
import { BooleanParam, NumberParam, useQueryParam } from 'use-query-params';

import { CalculateFeaturesJob, JobType, NucleaiExecutorJob } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { getActiveAnnotationAssignmentIdViewerKey } from 'services/annotations/useAnnotationQueryParams';
import { useSecondaryAnalysis } from 'services/secondaryAnalysis';
import { getRelevantSecondaryAnalysisSelectionModes } from 'services/secondaryAnalysis/secondaryAnalysisSelectionModes';
import { usePermissions } from 'utils/usePermissions';

export const geojsonFeaturesToMultiPolygon = (features: Feature[]) => {
  const coordinates: any[] = flatMap(features, (feature: Feature) => (feature.geometry as Geometry).coordinates);
  return {
    type: 'MultiPolygon',
    coordinates: coordinates,
  };
};
interface SecondaryAnalysisTriggerProps {
  slideId: string;
  viewerIndex: number;
  autoInternallyApprove: boolean;
  secondaryAnalysisSourceOrchestrationId: string;
  studyId: string;
  hasMultiplePublishedOrchestrationIds?: boolean;
  wholeSlideAnalysisJob?: CalculateFeaturesJob | NucleaiExecutorJob;
}

export const SecondaryAnalysisTrigger: React.FunctionComponent<
  React.PropsWithChildren<SecondaryAnalysisTriggerProps>
> = ({
  wholeSlideAnalysisJob,
  viewerIndex,
  secondaryAnalysisSourceOrchestrationId,
  hasMultiplePublishedOrchestrationIds,
  studyId,
}) => {
  useSignals();
  const { hasPermission } = usePermissions();
  const canRunSecondaryAnalysis = hasPermission(Permission.RunSecondaryAnalysis);
  const [useOSDViewer] = useQueryParam('useOSDViewer', BooleanParam);
  const [activeAnnotationAssignmentId] = useQueryParam(
    getActiveAnnotationAssignmentIdViewerKey(viewerIndex),
    NumberParam
  );
  const {
    context: {
      secondaryAnalysisSourceOrchestrationId: currentSecondaryAnalysisSourceOrchestrationId,
      secondaryAnalysisActiveViewer,
    },
    modifyContext: {
      clearSecondaryAnalysisOrchestrationIdQueryParams,
      setSecondaryAnalysisOrchestrationIdForViewer,
      setSecondaryAnalysisAreaSelectionMode,
    },
  } = useSecondaryAnalysis({ studyId });

  const hasActiveSelectionForSecondaryAnalysis =
    secondaryAnalysisActiveViewer === viewerIndex &&
    currentSecondaryAnalysisSourceOrchestrationId === secondaryAnalysisSourceOrchestrationId;

  const isOtherSecondaryAnalysisActive =
    currentSecondaryAnalysisSourceOrchestrationId && !hasActiveSelectionForSecondaryAnalysis;

  const secondaryAnalysisOptions = getRelevantSecondaryAnalysisSelectionModes();

  return (
    !useOSDViewer &&
    canRunSecondaryAnalysis && (
      <Tooltip
        title={
          hasMultiplePublishedOrchestrationIds
            ? `Multiple published orchestration ids - will run on ${secondaryAnalysisSourceOrchestrationId}`
            : ''
        }
      >
        <span>
          <Tooltip
            title={
              !secondaryAnalysisSourceOrchestrationId
                ? 'No orchestration id found'
                : activeAnnotationAssignmentId
                ? 'Cannot run secondary analysis while annotating'
                : isOtherSecondaryAnalysisActive
                ? 'Cannot run secondary analysis while other secondary analysis is active'
                : !wholeSlideAnalysisJob
                ? 'No job found'
                : !includes(
                    [JobType.PostProcessingAndCalculateFeatures, JobType.NucleaiExecutor],
                    wholeSlideAnalysisJob.type
                  )
                ? `Whole slide analysis job is not a CalculateFeatures or a study assigned algorithm with final results, got ${wholeSlideAnalysisJob.type}`
                : ''
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={
                  hasActiveSelectionForSecondaryAnalysis ? (
                    <CloseIcon fontSize="small" />
                  ) : (
                    <HighlightAltIcon fontSize="small" />
                  )
                }
                disabled={
                  !wholeSlideAnalysisJob || Boolean(activeAnnotationAssignmentId) || isOtherSecondaryAnalysisActive
                }
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasActiveSelectionForSecondaryAnalysis) {
                    clearSecondaryAnalysisOrchestrationIdQueryParams();
                  } else {
                    setSecondaryAnalysisOrchestrationIdForViewer(viewerIndex, secondaryAnalysisSourceOrchestrationId);
                    setSecondaryAnalysisAreaSelectionMode((previousMode) =>
                      // Don't change the previous mode, if set
                      find(secondaryAnalysisOptions, { editType: previousMode })
                        ? previousMode
                        : find(secondaryAnalysisOptions, { isDefault: true })?.editType
                    );
                  }
                }}
              >
                {hasActiveSelectionForSecondaryAnalysis
                  ? // Nothing is running yet at this stage, only the area-selection UI is open, so "Cancel"
                    // would incorrectly imply an in-progress job is being stopped.
                    'Close Secondary Analysis'
                  : 'Annotate for Secondary Analysis'}
              </Button>
            </span>
          </Tooltip>
        </span>
      </Tooltip>
    )
  );
};

export default SecondaryAnalysisTrigger;
